import { JSX } from 'react';

import { Table, TableBody } from '@/common/components/ui/table';
import { useScroll } from '@/policy/hooks/useScroll';
import { PolicyCoverage } from '@/policy/types/PolicyCoverage';

import { ScrollShadows } from '../ScrollShadows/ScrollShadows';
import { PolicyCoverageRow } from './PolicyCoverageRow';
import { PolicyCoverageTableHeader } from './PolicyCoverageTableHeader';

interface PolicyCoverageTableProps {
  sections: PolicyCoverage[];
}

const HIERARCHY_PADDING = 16;

export const PolicyCoverageTable = ({ sections }: PolicyCoverageTableProps) => {
  const { scrollContainerRef, dragProps, canScrollLeft, canScrollRight } =
    useScroll();

  const renderRows = (
    coverages: PolicyCoverage[],
    paddingLeft: number = HIERARCHY_PADDING,
  ) => {
    const rows: JSX.Element[] = [];

    for (const coverage of coverages) {
      rows.push(
        <PolicyCoverageRow
          key={coverage.uuid}
          coverage={coverage}
          paddingLeft={paddingLeft}
        />,
        ...renderRows(coverage.sections ?? [], paddingLeft + HIERARCHY_PADDING),
      );
    }

    return rows;
  };

  return (
    <ScrollShadows
      canScrollLeft={canScrollLeft}
      canScrollRight={canScrollRight}
    >
      <div
        ref={scrollContainerRef}
        {...dragProps}
        className={`w-full overflow-x-auto select-none @container ${dragProps.className}
        [&::-webkit-scrollbar]:h-1.5
        [&::-webkit-scrollbar-track]:bg-transparent
        [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
        [&::-webkit-scrollbar-thumb]:rounded-full`}
      >
        <Table
          noWrapper
          className="min-w-max w-full"
          style={{ tableLayout: 'auto' }}
        >
          <PolicyCoverageTableHeader />
          <TableBody>{renderRows(sections)}</TableBody>
        </Table>
      </div>
    </ScrollShadows>
  );
};

PolicyCoverageTable.displayName = 'PolicyCoverageTable';
